import React, { useEffect, useMemo, useState } from 'react';
import { Button, Input, Typography, showToast } from '@attentive-platform/stem-ui';

import FormScreen from '../CustomerScopeForm/FormScreen';
import ReviewScreen from '../CustomerScopeForm/ReviewScreen';
import useFormDraft from '../CustomerScopeForm/useFormDraft';
import { selectedModifierIdsFromDraft, selectedScopeIdsFromDraft } from '../CustomerScopeForm/scopeSelection';
import { normalizeCatalog } from '../../Utils/scopeCatalog';
import { fetchScopeCatalog, fetchScopeRecord, submitCompanyScopes } from './api';
import { FillOnBehalfViewStyles as S } from './styles';

const hasAnswer = ans => {
  if (!ans) return false;
  const opts = ans.selected_options || [];
  const text = ans.text_value || '';
  return opts.length > 0 || text.length > 0;
};

const FillOnBehalfView = ({ recordId, actor: actorProp, onBack, onSubmit }) => {
  const actor = (actorProp || localStorage.getItem('userEmail') || '').trim();
  const [record, setRecord] = useState(null);
  const [trades, setTrades] = useState([]);
  const [activeTradeIdx, setActiveTradeIdx] = useState(0);
  const [step, setStep] = useState('form');
  const [internalNote, setInternalNote] = useState('');
  const [noteTouched, setNoteTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [loading, setLoading] = useState(true);

  const { draft, toggleScope, toggleElement, setModifierAnswer, setNote, clearTrade } = useFormDraft({
    persist: false,
    tradesInScope: record?.trades_in_scope
  });

  useEffect(() => {
    const previous = document.title;
    document.title = 'Fill on behalf · Customer Scope Form';
    return () => {
      document.title = previous;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([fetchScopeRecord(recordId), fetchScopeCatalog()]).then(
      ([{ data: recordData }, { data: catalogData }]) => {
        if (cancelled) return;
        if (recordData) setRecord(recordData);
        if (catalogData) setTrades(normalizeCatalog(catalogData).trades || []);
        setLoading(false);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [recordId]);

  useEffect(() => {
    if (!trades.length) return;
    if (activeTradeIdx >= trades.length) setActiveTradeIdx(0);
  }, [trades, activeTradeIdx]);

  const headerTitle = useMemo(
    () =>
      record?.prospect_company_name ||
      record?.company_name ||
      record?.customer_email ||
      'Fill on behalf',
    [record]
  );

  const handleEditTrade = tradeId => {
    const idx = trades.findIndex(t => t.trade_id === tradeId);
    if (idx >= 0) setActiveTradeIdx(idx);
    setStep('form');
    window.scrollTo({ top: 0 });
  };

  const handleSubmit = async () => {
    const scope_ids = selectedScopeIdsFromDraft(trades, draft);
    if (!scope_ids.length) {
      showToast('Pick at least one item before submitting', 'error');
      return;
    }
    if (!actor) {
      showToast('Missing signed-in user email — cannot record who filled this.', 'error');
      return;
    }
    const trimmedInternalNote = internalNote.trim();
    if (!trimmedInternalNote) {
      setNoteTouched(true);
      showToast('Internal note is required', 'error');
      return;
    }
    const element_ids = Object.keys(draft.elements);
    const allowedModifiers = selectedModifierIdsFromDraft(trades, draft);
    const modifier_answers = Object.entries(draft.modifiers)
      .filter(([modifier_id, v]) => allowedModifiers.has(modifier_id) && hasAnswer(v))
      .map(([modifier_id, v]) => ({
        modifier_id,
        selected_options: v.selected_options || [],
        text_value: v.text_value || ''
      }));
    const notesEntries = Object.entries(draft.notes || {})
      .map(([tradeId, value]) => [tradeId, (value || '').trim()])
      .filter(([, value]) => value.length > 0);
    const notes_data = notesEntries.length ? Object.fromEntries(notesEntries) : undefined;
    setSubmitting(true);
    setSubmitError('');
    const { data, error } = await submitCompanyScopes({
      scope_ids,
      element_ids,
      modifier_answers,
      notes_data,
      internal_note: trimmedInternalNote,
      card_id: recordId,
      actor_email: actor
    });
    setSubmitting(false);
    if (error) {
      const msg =
        typeof error === 'string' ? error : error.detail || 'Submit failed — please try again.';
      setSubmitError(msg);
      showToast(msg, 'error');
      return;
    }
    showToast(`Submitted on behalf — recorded as ${actor}`, 'success');
    onSubmit && onSubmit({ scope_ids, element_ids, modifier_answers, notes_data, response: data });
  };

  if (loading) {
    return (
      <S.LoadingWrap>
        <Typography variant='body2' color='#666666'>
          Loading scope form…
        </Typography>
      </S.LoadingWrap>
    );
  }

  const noteMissing = !internalNote.trim();
  const noteError = noteTouched && noteMissing;

  return (
    <S.Root>
      <S.Ribbon>
        <S.RibbonLeft>
          <S.Pill>On behalf</S.Pill>
          <Typography variant='body2' sx={{ fontWeight: 600 }}>
            Filling for {record?.customer_email || 'the customer'} — recorded as {actor || '—'}
          </Typography>
        </S.RibbonLeft>
        <S.Actions>
          <Button variant='text' color='primary' size='medium' onClick={onBack} disabled={submitting}>
            Back to record
          </Button>
        </S.Actions>
      </S.Ribbon>

      {step === 'form' ? (
        <FormScreen
          trades={trades}
          activeTradeIdx={activeTradeIdx}
          onSelectTrade={setActiveTradeIdx}
          draft={draft}
          savedAt={null}
          onToggleScope={toggleScope}
          onToggleElement={toggleElement}
          onModifierChange={setModifierAnswer}
          onNote={setNote}
          onClearTrade={clearTrade}
          onReview={() => {
            setStep('review');
            window.scrollTo({ top: 0 });
          }}
          readOnly={false}
          state={null}
          headerTitle={headerTitle}
        />
      ) : (
        <ReviewScreen
          trades={trades}
          draft={draft}
          email={record?.customer_email || ''}
          onEmail={() => {}}
          onEditTrade={handleEditTrade}
          onBackToForm={() => {
            setStep('form');
            window.scrollTo({ top: 0 });
          }}
          onSubmit={handleSubmit}
          submitting={submitting}
          submitError={submitError}
          readOnly={false}
          onBehalf
          submitExtraDisabled={noteMissing}
          submitExtra={
            <S.InternalNoteBlock>
              <S.InternalNoteLabel>
                Internal note <S.RequiredMark>*</S.RequiredMark>
              </S.InternalNoteLabel>
              <S.InternalNoteHint>Required. Never shown to the customer. Recorded with your name.</S.InternalNoteHint>
              <Input
                size='large'
                multiline
                minRows={3}
                value={internalNote}
                onChange={e => {
                  setInternalNote(e.target.value);
                  if (!noteTouched) setNoteTouched(true);
                }}
                onBlur={() => setNoteTouched(true)}
                placeholder='e.g. filled from kickoff call'
                error={noteError}
              />
              {noteError && <S.InternalNoteError>Internal note is required</S.InternalNoteError>}
            </S.InternalNoteBlock>
          }
        />
      )}
    </S.Root>
  );
};

export default FillOnBehalfView;
